import Manager from 'coreClasses/Manager';

import CountryModel from 'coreClasses/countries/CountryModel';
import LanguageModel from 'coreClasses/languages/LanguageModel';
import CookiesMgr from 'coreClasses/cookies/CookiesMgr';
import Cookie from 'coreClasses/cookies/Cookie';
import {PREFERRED_COUNTRY, PREFERRED_LANGUAGE} from 'coreClasses/cookies/config';
import {IGeolocation, TGeolocationResponse} from 'coreClasses/geolocation/IGeolocation';
import {
	receiveConfigurations,
	receiveCurrentCountryCode,
	receiveCurrentLanguageCode,
	receiveSiteChangingStatus,
	selectCurrentCountryCode,
	selectCurrentLanguageCode,
} from 'coreStore/data/configurations/index';

import {getParamsFromPath, getUrlConfig, isServer, getMappedCountryCodeByLanguage} from 'coreUtils/utils';
import {rebuildPathWithParams, getDecodedURI, parseURL} from 'coreUtils/url';

import {getDefaultSite} from 'coreUtils/siteUtils';
import {getLocalizablePathNameByPath} from 'brand/app/utils/routes-utils';
import {HOME_HREF, urlPartPositions} from 'brand/app/constants';

import LocaleModel from './LocaleModel';
import {receiveBasket} from '../../store/data/basket';

enum ELocalesMgrReduxStorageType {
	PRODUCTS,
}

const reduxCleanupMapping = {
	language: [ELocalesMgrReduxStorageType.PRODUCTS]
};

type TLocationDescriptor = {
	pathname?: string;
	search?: string;
	hash?: string;
	state?: unknown;
};

interface IPathWithLocaleOpts {
	location?: string | TLocationDescriptor;
	language?: string;
	disallowParams?: string[];
}

interface IUrlConfig {
	showDefaults?: boolean;
	site?: string;
	locale?: string;
	[key: string]: string | boolean | undefined;
}

interface IDefaultSite {
	id?: string;
	alias?: string;
	defaultLocale?: string;
	[key: string]: string | undefined;
}

/**
 * Represents LocalesMgr
 */
class LocalesMgr extends Manager {
	private reduxCleanupItems: Set<ELocalesMgrReduxStorageType> = new Set();
	private _lockUrlUpdate: boolean = false;
	private _urlConfig: IUrlConfig = null;
	private _defaultSite: IDefaultSite = null;
	private _defaultLanguage: LanguageModel = null;
	private _defaultCountry: CountryModel = null;
	private _defaultLocale: LocaleModel = null;

	/**
	 * Initialize locales manager
	 * @returns {Promise<void>}
	 */
	async init(): Promise<any> {
		return await Promise.all([
			this.managerRepository.getCountriesMgr().fetchAllCountries(),
			this.managerRepository.getLanguagesMgr().fetchAllLanguages(),
		]);
	}

	/**
	 * Sets current country
	 * @param {CountryModel} country - country model
	 * @returns {Promise<any>}
	 */
	async setCountry(country: CountryModel | string): Promise<any> {
		if (typeof country === 'string') {
			country = this.managerRepository.getCountriesMgr().getCountryByCode(country);
		}

		const countryCode = country.getCountryCode();

		if (!isServer && country?.getCurrencyCode()) {
			localStorage.setItem('scapi:currency', country.getCurrencyCode());
		}

		const prevCurrency = this.getCurrentCountry()?.getCurrencyCode();
		const currentCountry = country || this.getCurrentCountry();
		const siteId: string = currentCountry.getSiteId();
		const currentSiteId: string = this.managerRepository.getSfccAPI().getSiteId();
		let isSiteChanged = false;

		this.managerRepository.getCommerceApi().setCurrency(country.getCurrencyCode());

		if (siteId && currentSiteId !== siteId) {
			// This removes the basket locally, but not the remote one - Temporary because useBasket is not accessible from here
			// THIS MUST RUN BEFORE COUNTRY CHANGE
			this.managerRepository.getStoreMgr().getStore().dispatch(receiveBasket(null));
		}

		this.managerRepository.getStoreMgr().getStore().dispatch(receiveCurrentCountryCode(countryCode));
		this.managerRepository.getSfccAPI().setCountry(countryCode);

		if (siteId && currentSiteId !== siteId) {
			// Dispatch site changing. Site changed will be dispatched after locale update on useLocalesMgr
			this.managerRepository.getStoreMgr().getStore().dispatch(receiveSiteChangingStatus('changing'));
			await this.processSiteChange(siteId);

			isSiteChanged = true;
		}

		const awaitPromises = [];

		if (prevCurrency !== country.getCurrencyCode() || Object.keys(country.getCurrencyConfig()).length) {
			awaitPromises.push(this.managerRepository.getCountriesMgr().updateCurrencyConversionRates(countryCode));
		}

		if (isServer) {
			//On the client side this is already managed via useShopper initialized in App.tsx
			awaitPromises.push(
				this.managerRepository.getCustomerMgr().updateCustomerContextQualifiers({
					shippingCountry: countryCode,
				} as any),
			);
		}

		await Promise.all(awaitPromises);

		return {
			isSiteChanged,
		};
	}

	/**
	 * Sets current language
	 * @param {LanguageModel} language - language model
	 * @returns {Promise<void>}
	 */
	async setLanguage(language: LanguageModel | string): Promise<void> {
		if (typeof language === 'string') {
			try {
				language = this.managerRepository.getLanguagesMgr().getLanguageByCode(language);
			} catch (e) {
				console.error(`The language with code ${language} not found. The language was not changed.`);

				return Promise.resolve();
			}
		}

		const languageCode = language.getLanguageCode();

		if (!isServer) {
			reduxCleanupMapping.language.forEach((type) => {
				this.reduxCleanupItems.add(type);
			});
			localStorage.setItem('scapi:locale', languageCode);
		}

		this.managerRepository.getStoreMgr().getStore().dispatch(receiveCurrentLanguageCode(languageCode));
		this.managerRepository.getCommerceApi().setLocale(languageCode);
		this.managerRepository.getSfccAPI().setLocale(languageCode);

		if (this.getCurrentLanguage()?.getLanguageCode() !== languageCode) {
			this.managerRepository.getStoreMgr().getStore().dispatch(receiveCurrentLanguageCode(languageCode));

			if (!isServer) {
				reduxCleanupMapping.language.forEach((type) => {
					this.reduxCleanupItems.add(type);
				});
				localStorage.setItem('scapi:locale', languageCode);
			}
		}

		return Promise.resolve();
	}

	/**
	 * Updates current locale
	 */
	updateReduxStore() {
		this.reduxCleanupItems.forEach((type) => {
			switch (type) {
				case ELocalesMgrReduxStorageType.PRODUCTS:
					this.managerRepository.getProductsMgr().markProductsToBeRefreshed();

					break;
			}
		});
		this.reduxCleanupItems.clear();
	}

	/**
	 * Returns current country
	 * @returns {CountryModel | null} - current country
	 */
	getCurrentCountry(): CountryModel {
		const currentCountryCode = selectCurrentCountryCode(this.managerRepository.getStoreMgr().getStore().getState());

		if (currentCountryCode) {
			return this.managerRepository.getCountriesMgr().getCountryByCode(currentCountryCode);
		}

		return null;
	}

	/**
	 * Returns current language
	 * @returns {LanguageModel | null} - current language
	 */
	getCurrentLanguage(): LanguageModel {
		const currentLanguageCode = selectCurrentLanguageCode(
			this.managerRepository.getStoreMgr().getStore().getState(),
		);

		if (currentLanguageCode) {
			return this.managerRepository.getLanguagesMgr().getLanguageByCode(currentLanguageCode);
		}

		return null;
	}

	/**
	 * Returns current language Code
	 * @param {boolean} isDefault - is default fallback
	 * @returns {string | null} - current language code
	 */
	getCurrentLanguageCode(isDefault = false): string | null {
		const currentLocaleModel = this.getCurrentLocale();

		if (currentLocaleModel) {
			return currentLocaleModel.getLanguageCode();
		}

		return isDefault ? this.getDefaultLocale().getLanguageCode() : null;
	}

	/**
	 * Returns current country Code
	 * @param {boolean} isDefault - is default fallback
	 * @returns {string | null} - current country Code
	 */
	getCurrentCountryCode(isDefault = false): string | null {
		const currentLocaleModel = this.getCurrentLocale();

		if (currentLocaleModel) {
			return currentLocaleModel.getCountryCode();
		}

		return isDefault ? this.getDefaultLocale().getCountryCode() : null;
	}

	/**
	 * Converts given link into PWA format
	 * @param {LocationDescriptor|String} link - SFCC link with [languageCode]
	 * @param {LocaleModel} locale - locale model
	 * @returns {String} - updated link [countryCode]-[languageCode]
	 */
	updateLinkWithLocale(link: TLocationDescriptor | string, locale?: LocaleModel): string {
		const currentLocale = locale || this.getCurrentLocale();

		return this.getPathWithLocale(currentLocale.getCode(), {
			location: link,
			language: currentLocale?.getLanguageCode(),
		});
	}

	/**
	 * Converts given link into SFCC format - without country code
	 * @param {string} link - link with full locale [countryCode]-[languageCode]
	 * @returns {String} - updated link without country code
	 */
	normalizeLink(link: string): string {
		const localeFromURL = this.parseLocaleFromURL('', link);
		let currentLocale;

		if (localeFromURL.isValid()) {
			currentLocale = localeFromURL;
		} else {
			currentLocale = this.getCurrentLocale();
		}

		const pathWithLocale = this.getPathWithLocale(currentLocale.getLanguageCode(), {location: link});

		return getDecodedURI(pathWithLocale);
	}

	/**
	 * Returns url without locale
	 * @param {string} url - url with locale
	 * @returns {string} - url without locale
	 */
	getUrlWithoutLocale(url: string): string {
		return this.getPathWithLocale(null, {location: url});
	}

	/**
	 * Returns current locale
	 * @returns {LocaleModel} - current locale
	 */
	getCurrentLocale(): LocaleModel {
		return new LocaleModel(this.getCurrentCountry(), this.getCurrentLanguage());
	}

	/**
	 * Returns current locale string
	 * @returns {string} - current locale (ex: en_IT)
	 */
	getCurrentLocaleString(delimiter: string = '_'): string {
		if (this.getCurrentCountryCode() && this.getCurrentLanguageCode()) {
			return `${this.getCurrentLanguageCode()}${delimiter}${this.getCurrentCountryCode()}`;
		} else {
			return null;
		}
	}

	/**
	 * Returns locale object
	 * @returns {LocaleModel} - locale object
	 */
	getLocale(countryCode: string, languageCode: string): LocaleModel {
		return new LocaleModel(
			this.managerRepository.getCountriesMgr().getCountryByCode(countryCode),
			this.managerRepository.getLanguagesMgr().getLanguageByCode(languageCode),
		);
	}

	/**
	 * Returns default locales data
	 * @returns {LocaleModel} - locale data if available
	 */
	getDefaultLocale(): LocaleModel {
		if (!this._defaultLocale) {
			this._defaultLocale = new LocaleModel(this.getDefaultCountry(), this.getDefaultLanguage());
		}

		return this._defaultLocale;
	}

	/**
	 * Returns locale data from URL
	 * @param {string} hostname - Current hostname
	 * @param {string} url - Current url
	 * @returns {LocaleModel} - locale data if available
	 */
	parseLocaleFromURL(hostname: string, url: string): LocaleModel {
		const {localeRef} = getParamsFromPath(url);
		let countryCode;
		let languageCode;

		if (localeRef) {
			[languageCode, countryCode] = localeRef.split('-');

			if (languageCode && countryCode) {
				countryCode = countryCode.toUpperCase();
			} else if (languageCode && !countryCode) {
				countryCode = getMappedCountryCodeByLanguage(hostname, languageCode);
			}
		}

		return new LocaleModel(
			countryCode && this.managerRepository.getCountriesMgr().getCountryByCode(countryCode),
			languageCode && this.managerRepository.getLanguagesMgr().getLanguageByCode(languageCode),
		);
	}

	/**
	 * Returns locale data from Cookie
	 * @returns {LocaleModel} - locale data if available
	 */
	parseLocaleFromCookies(): LocaleModel {
		const countryCode = CookiesMgr.getCookie(PREFERRED_COUNTRY.cookieName).getValue();
		const languageCode = CookiesMgr.getCookie(PREFERRED_LANGUAGE.cookieName).getValue();
		// Cookiebot can set up undefined to country or language
		const country =
			countryCode &&
			countryCode !== 'undefined' &&
			this.managerRepository.getCountriesMgr().getCountryByCode(countryCode);
		const language =
			languageCode &&
			languageCode !== 'undefined' &&
			this.managerRepository.getLanguagesMgr().getLanguageByCode(languageCode);

		return new LocaleModel(country, language);
	}

	/**
	 * Fetches geolocation
	 * @returns {Promise<IGeolocation|null>} - geolocation
	 */
	async fetchGeolocation(): Promise<IGeolocation | null> {
		return this.managerRepository
			.getGeolocationMgr()
			.getGeolocation()
			.then((geolocationData: TGeolocationResponse) => {
				if (!geolocationData.country) {
					return Promise.resolve(null);
				} else {
					return {
						...geolocationData,
						country: this.managerRepository.getCountriesMgr().getCountryByCode(geolocationData.country),
					};
				}
			});
	}

	/**
	 * Updates cookies with current values
	 */
	updateCookiesWithCurrentValues(): void {
		const options = {
			domain: null,
			expires: null,
		};
		const cookieDomain = window.location.host.split('.').slice(-2);

		if (cookieDomain.length > 1) {
			options.domain = `.${cookieDomain.join('.')}`;
		}

		const currentLocale = this.getCurrentLocale();

		if (currentLocale.isValid()) {
			options.expires = PREFERRED_COUNTRY.expiration;

			CookiesMgr.setCookie(new Cookie(PREFERRED_COUNTRY.cookieName, currentLocale.getCountryCode(), options));

			options.expires = PREFERRED_LANGUAGE.expiration;

			CookiesMgr.setCookie(new Cookie(PREFERRED_LANGUAGE.cookieName, currentLocale.getLanguageCode(), options));
			// duplicate also in session storage
			localStorage.setItem('scapi:currency', currentLocale.getCurrencyCode());
			localStorage.setItem('scapi:locale', currentLocale.getLanguageCode());
		}

		this.setCookiesProcessed();
	}

	/**
	 * Make all new setups due to changing Site
	 * @param {string} siteId - Site ID to be applied
	 * @returns {Promise<void>} - promise resolve
	 */
	private async processSiteChange(siteId): Promise<void> {
		this.managerRepository.getCommerceApi().setSiteId(siteId);
		this.managerRepository.getSfccAPI().setSiteId(siteId);
		this.managerRepository.getOrdersMgr().cleanupOrders();

		const updatedConfigurations = await this.managerRepository.getSfccAPI().getConfigurations();

		this.managerRepository.getConfigurationsMgr().invalidateAllCache();
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveConfigurations(updatedConfigurations));

		// Update Einstean API configuration
		const einsteinAPI = this.managerRepository.getCommerceApi().getEinsteinAPI();
		const configurationsMgr = this.managerRepository.getConfigurationsMgr();
		einsteinAPI.setEinsteinSiteId(configurationsMgr.get('sitePreferences.einsteinSiteId'));
		einsteinAPI.setEinsteinClientId(configurationsMgr.get('sitePreferences.einsteinClientId'));

		await Promise.all([
			this.managerRepository.getCountriesMgr().fetchAllCountries(),
			this.managerRepository.getLanguagesMgr().fetchAllLanguages(),
		]);

		return Promise.resolve();
	}

	/**
	 * Locks automatic URL update on locale changes
	 * @param {boolean} lockFlag - new value
	 * @returns {boolean}
	 */
	lockUrlUpdate(lockFlag: boolean): boolean {
		return (this._lockUrlUpdate = lockFlag);
	}

	/**
	 * Returns true if automatic URL update is locked
	 * @returns {boolean}
	 */
	isUrlUpdateLocked(): boolean {
		return this._lockUrlUpdate;
	}

	/**
	 * Returns a relative URL for a locale short code.
	 * Based on your app configuration, this function will replace your current locale shortCode with a new one
	 *
	 * @param {String} shortCode - The locale short code.
	 * @param {Object} [opts] - Options, if there's any.
	 * @param {Object} opts.location - location object to replace the default `window.location`
	 * @returns {String} url - The relative URL for the specific locale.
	 */
	private getPathWithLocale = (shortCode: string, opts: IPathWithLocaleOpts = {}): string => {
		let location: string | TLocationDescriptor = opts.location ?? (isServer ? '' : window.location.href);
		let {pathname, search} = parseURL(location as string);
		let {siteRef, localeRef} = getParamsFromPath(`${pathname}${search}`);
		const language = opts?.language;

		// sanitize the site from current url if existing
		if (siteRef) {
			// Covers urls from sandbox
			pathname = pathname.replace(`/s/${siteRef}/`, '/');

			if (search) {
				search = search.replace(`site=${siteRef}`, '');
			}
		}

		// sanitize the locale from current url if existing
		if (localeRef) {
			pathname = pathname.replace(`/${localeRef}`, '');

			if (search) {
				search = search.replace(`locale=${localeRef}`, '');
			}
		}
		// remove ending any &

		if (language) {
			const foundSubObject = getLocalizablePathNameByPath(pathname);

			if (foundSubObject) {
				pathname = foundSubObject[language];
			}
		}

		if (search) {
			search = search.replace(/&$/, '');
		}

		const defaultSite = this.getDefaultSite();
		const isHomeRef = pathname === HOME_HREF;
		const isDefaultLocaleOfDefaultSite = shortCode === defaultSite.defaultLocale;
		const isDefaultSite = siteRef === defaultSite.alias || siteRef === defaultSite.id;
		// rebuild the url with new locale,

		return this.buildPathWithUrlConfig(
			`${pathname}${search}`,
			{
				// By default, as for home page, when the values of site and locale belongs to the default site,
				// they will be not shown in the url just
				site:
					isDefaultLocaleOfDefaultSite && isDefaultSite && isHomeRef
						? ''
						: siteRef || defaultSite.alias || defaultSite.id,
				locale: isDefaultLocaleOfDefaultSite && isDefaultSite && isHomeRef ? '' : shortCode,
			},
			opts,
		);
	};

	/**
	 * Rebuild the path with locale/site values with a given url
	 * The position of those values will based on the url config of your current app configuration.
	 *
	 * @param {string} relativeUrl - the base relative Url to be reconstructed on
	 * @param {object} configValues - object that contains values of url config
	 * @param {Object} [opts] - Options, if there's any.
	 * @param {string[]} opts.disallowParams - URL parameters to remove
	 * @return {string} - an output path that has locale and site
	 *
	 * @example
	 * // configuration
	 * url {
	 *    locale: "query_param",
	 *    site: "path",
	 *    showDefaults: true
	 * }
	 *
	 *
	 * const site = {
	 *   id: 'RefArch',
	 *   alias: 'global'
	 *   l10n: {
	 *     defaultLocale: 'en-GB'
	 *     supportedLocales: [
	 *       {id: 'en-GB', preferCurrency: 'GBP'}
	 *     ]
	 *     // other props
	 *   }
	 * }
	 * buildPathWithUrlConfig('/women/dresses', {locale: 'en-GB', site: 'global'})
	 * => /global/women/dresses?locale=en-GB
	 *
	 */

	/**
	 * Returns the URL config
	 * @returns {object}
	 */
	getUrlConfig(): IUrlConfig {
		if (!this._urlConfig) {
			this._urlConfig = getUrlConfig() as IUrlConfig;
		}

		return this._urlConfig;
	}

	/**
	 * Returns the default site
	 * @returns {object}
	 */
	getDefaultSite(): IDefaultSite {
		if (!this._defaultSite) {
			this._defaultSite = getDefaultSite() as IDefaultSite;
		}

		return this._defaultSite;
	}

	/**
	 * Returns the default country
	 * @returns {CountryModel}
	 */
	getDefaultCountry(): CountryModel {
		if (!this._defaultCountry) {
			this._defaultCountry = this.managerRepository.getCountriesMgr().getDefaultCountry();
		}

		return this._defaultCountry;
	}

	/**
	 * Returns the default language
	 * @returns {LanguageModel}
	 */
	getDefaultLanguage(): LanguageModel {
		if (!this._defaultLanguage) {
			this._defaultLanguage = this.managerRepository.getLanguagesMgr().getDefaultLanguage();
		}

		return this._defaultLanguage;
	}

	private buildPathWithUrlConfig = (relativeUrl, configValues: Record<string, string> = {}, opts: IPathWithLocaleOpts = {}) => {
		const urlConfig = this.getUrlConfig();
		const defaultSite = this.getDefaultSite();
		const defaultLanguage = this.getDefaultLanguage();
		const defaultLocaleRefs = defaultLanguage.getLanguageCode();
		const {disallowParams = []} = opts;
		let params;
		let queryParams = {};

		if (!Object.values(configValues).length) {
			return relativeUrl;
		}

		const [pathname, search] = relativeUrl.split('?');

		if (search) {
			params = new URLSearchParams(search);
		}

		// Remove any disallowed params.
		if (params && disallowParams.length) {
			disallowParams.forEach((param) => {
				params.delete(param);
			});
		}

		let basePathSegments = [];
		// get the default values for site and locale
		const showDefaults = urlConfig.showDefaults;
		const defaultSiteRefs = [defaultSite.id, defaultSite.alias];
		const defaultValues = [...defaultSiteRefs, ...defaultLocaleRefs];
		const options = ['site', 'locale'];

		if (params) {
			queryParams = {...Object.fromEntries(params)};
		}

		options.forEach((option) => {
			const position = urlConfig[option] || urlPartPositions.NONE;
			const val = configValues[option];

			if (position === urlPartPositions.PATH) {
				// if showDefaults is false, the default value will not be show in the url
				if (!showDefaults && defaultValues.includes(val)) {
					return;
				}

				basePathSegments.push(val);
			} else if (position === urlPartPositions.QUERY_PARAM) {
				// if showDefaults is false, the default value will not be show in the url
				if (!showDefaults && defaultValues.includes(val)) {
					return;
				}

				if (queryParams) {
					queryParams[option] = val;
				}
			}
		});

		// filter out falsy (empty string, undefined, null, etc) values in the array
		basePathSegments = basePathSegments.filter(Boolean);

		let updatedPath = `${basePathSegments.length ? `/${basePathSegments.join('/')}` : ''}${pathname}`;

		// append the query param to pathname
		if (queryParams && Object.keys(queryParams).length) {
			updatedPath = rebuildPathWithParams(updatedPath, queryParams);
		}

		return updatedPath;
	};
}

export default LocalesMgr;
